// Judo journal data — taxonomy, factories, and helpers.
//
// Source-of-truth: /app/memory/JUDO_RESEARCH.md (judoinfo.com canonical
// classification). Judo classifies techniques BY MECHANISM, not by
// position context like BJJ — the structure is intentionally different
// (a forked agent's "make Judo look like BJJ" instinct would be wrong).
//
// Belt rank system: IJF (International Judo Federation) — international
// governing body. Future toggle infrastructure for alternate systems
// (Kodokan original / USA-Judo / USJA Western progression) is its own
// backlog item; v1 ships single-system IJF.

// --- Taxonomy: group → subgroup → curated technique list ---
export const JUDO_GROUPS = [
  {
    group: 'Nage-waza',
    groupLabel: 'Nage-waza (Throwing)',
    subgroups: [
      {
        subgroup: 'Te-waza',
        subgroupLabel: 'Te-waza (Hand)',
        techniques: [
          'Tai Otoshi',
          'Ippon Seoinage',
          'Seoi Nage',
          'Morote Seoinage',
          'Kata Guruma',
          'Sukui Nage',
          'Uki Otoshi',
        ],
      },
      {
        subgroup: 'Ashi-waza',
        subgroupLabel: 'Ashi-waza (Foot / Leg)',
        techniques: [
          'Uchi Mata',
          'Ouchi Gari',
          'Ko Uchi Gari',
          'O Soto Gari',
          'Ko Soto Gari',
          'De Ashi Harai',
          'Okuri Ashi Harai',
          'Sasae Tsurikomi Ashi',
          'Hane Goshi',
          'Hiza Guruma',
          'Harai Tsurikomi Ashi',
        ],
      },
      {
        subgroup: 'Koshi-waza',
        subgroupLabel: 'Koshi-waza (Hip)',
        techniques: [
          'Harai Goshi',
          'Tsuri Goshi',
          'O Goshi',
          'Koshi Guruma',
          'Tsurikomi Goshi',
          'Sode Tsurikomi Goshi',
          'Uki Goshi',
          'Utsuri Goshi',
        ],
      },
      {
        subgroup: 'Ma-sutemi-waza',
        subgroupLabel: 'Ma-sutemi-waza (Back Sacrifice)',
        techniques: [
          'Tomoe Nage',
          'Sumi Gaeshi',
          'Hikikomi Gaeshi',
          'Tawara Gaeshi',
        ],
      },
      {
        subgroup: 'Yoko-sutemi-waza',
        subgroupLabel: 'Yoko-sutemi-waza (Side Sacrifice)',
        techniques: [
          'Yoko Otoshi',
          'Tani Otoshi',
          'Hane Makikomi',
          'Soto Makikomi',
          'Uki Waza',
        ],
      },
    ],
  },
  {
    group: 'Katame-waza',
    groupLabel: 'Katame-waza (Grappling)',
    subgroups: [
      {
        subgroup: 'Osaekomi-waza',
        subgroupLabel: 'Osaekomi-waza (Pinning)',
        techniques: [
          'Kesa Gatame',
          'Kami Shiho Gatame',
          'Yoko Shiho Gatame',
          'Tate Shiho Gatame',
          'Kuzure Kesa Gatame',
          'Mune Gatame',
        ],
      },
      {
        subgroup: 'Shime-waza',
        subgroupLabel: 'Shime-waza (Choking)',
        techniques: [
          'Hadaka Jime',
          'Okuri Eri Jime',
          'Kata Ha Jime',
          'Sankaku Jime',
          'Gyaku Juji Jime',
          'Nami Juji Jime',
          'Kata Juji Jime',
        ],
      },
      {
        subgroup: 'Kansetsu-waza',
        subgroupLabel: 'Kansetsu-waza (Joint Locks)',
        helpText: 'Kodokan / IJF rules restrict joint locks to the elbow. Other locks shown in some external sources are not standard Judo.',
        techniques: [
          'Juji Gatame',
          'Ude Garami',
          'Ude Gatame',
          'Hiza Gatame',
          'Waki Gatame',
        ],
      },
    ],
  },
];

// --- Helpers (cascading picker support) ---

export const getJudoSubgroupsFor = (group) => {
  const g = JUDO_GROUPS.find((g) => g.group === group);
  return g ? g.subgroups : [];
};

export const getJudoTechniquesFor = (group, subgroup) => {
  const subs = getJudoSubgroupsFor(group);
  const s = subs.find((s) => s.subgroup === subgroup);
  return s ? s.techniques : [];
};

export const formatJudoClassification = (cls) => {
  if (!cls) return '';
  const parts = [];
  if (cls.group) parts.push(cls.group);
  if (cls.subgroup) parts.push(cls.subgroup);
  if (cls.name) parts.push(cls.name);
  return parts.join(' · ');
};

// --- Constants ---

// Class formats — Judo-canonical. Order matches typical session flow.
export const JUDO_CLASS_FORMATS = [
  { value: 'uchikomi', label: 'Uchi-komi (entry drills)' },
  { value: 'nagekomi', label: 'Nage-komi (throw drills)' },
  { value: 'randori', label: 'Randori (free practice)' },
  { value: 'newaza-randori', label: 'Newaza-randori (groundwork sparring)' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'instruction', label: 'Instruction / Demo' },
];

export const JUDO_FOCUS_MODES = [
  'Throws',
  'Groundwork',
  'Combinations',
  'Counters',
  'Defense',
  'Conditioning',
];

// Mastery scale — universal across Combatives journals.
export const JUDO_MASTERY_LEVELS = [
  { value: 'Drilling', help: 'Working on form; not yet trying it live.' },
  { value: 'Live', help: 'Tried it in randori; some success, still inconsistent.' },
  { value: 'Mastered', help: 'Reliably lands in randori; part of my game.' },
];

// IJF-aligned belt order for adults. Some Western dojos insert
// intermediate colors for juniors; v1 ignores juniors. Black-belt
// progression uses Dan grades (1st through 10th).
export const JUDO_BELTS = [
  'White',
  'Yellow',
  'Orange',
  'Green',
  'Blue',
  'Brown',
  'Shodan (1st Dan Black)',
  'Nidan (2nd Dan)',
  'Sandan (3rd Dan)',
  'Yodan (4th Dan)',
  'Godan (5th Dan)',
];

// Felt-scale for randori — matches BJJ scale so cross-art muscle
// memory stays consistent for users training both.
export const JUDO_FELT_SCALE = [
  { value: 1, label: '1 · Outmatched, learning' },
  { value: 2, label: '2 · Working but defensive' },
  { value: 3, label: '3 · Even exchanges' },
  { value: 4, label: '4 · Imposed my game most rounds' },
  { value: 5, label: '5 · Sharp / flow state' },
];

// --- Factories ---

export const newJudoTechniqueRow = () => ({
  classification: { group: '', subgroup: '', name: '' },
  outcome: '',           // Drilled / Landed in randori / Countered (free-text)
  partnerLevel: '',      // Junior / Peer / Senior / Sensei (optional)
  reps: '',
  notes: '',
  mastery: 'Drilling',
});

export const newJudoRandoriStats = () => ({
  rounds: 0,
  throwsCompleted: 0,
  throwsCountered: 0,
  groundworkSubmissions: 0,
  groundworkSubmitted: 0,
  feltScale: '',
  partners: [],
  notes: '',
});

export const newJudoReflection = () => ({
  intentHit: '',
  win: '',
  gap: '',
  nextFocus: '',
});
